import { Observer } from '@playcanvas/observer';
import { Container, Spinner } from '@playcanvas/pcui/react';
import React from 'react';
import ReactDOM from 'react-dom';

import { ObserverData } from '../types';
import ErrorBox from './errors';
import LeftPanel from './left-panel';
import LoadControls from './load-controls';
import PopupPanel from './popup-panel';
import SelectedNode from './selected-node';
import ManifestDialog from './manifest-dialog';
import MenuBar from './menu-bar';
import AboutDialog from './about-dialog';

import { version as appVersion } from '../../package.json';

// Loading Overlay Component
class LoadingOverlay extends React.Component<{ 
    message: string,
    progress?: {
        current?: string,
        currentIndex?: number,
        total?: number,
        percentage?: number,
        size?: string,
    }
}> {
    formatBytes(bytes: number): string {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }

    render() {
        const { message, progress } = this.props;
        const percentage = progress?.percentage ?? (progress?.currentIndex !== undefined && progress?.total 
            ? Math.round((progress.currentIndex / progress.total) * 100) 
            : undefined);

        return (
            <>
                <style>{`
                    @keyframes loading-indeterminate {
                        0% {
                            transform: translateX(-100%);
                        }
                        50% {
                            transform: translateX(250%);
                        }
                        100% {
                            transform: translateX(-100%);
                        }
                    }
                `}</style>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    pointerEvents: 'all',
                }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxWidth: '500px',
                    padding: '0 20px',
                }}>
                    <div style={{
                        color: 'white',
                        fontSize: '16px',
                        fontFamily: 'Proxima Nova Regular, Helvetica Neue, Arial, Helvetica, sans-serif',
                        textAlign: 'center',
                        width: '100%',
                    }}>
                        <div style={{ marginBottom: '12px', fontWeight: 500 }}>{message}</div>
                        {progress?.current && (
                            <div style={{ 
                                fontSize: '14px', 
                                color: '#aaaaaa',
                                marginBottom: '8px',
                                wordBreak: 'break-word',
                            }}>
                                {progress.current}
                            </div>
                        )}
                        {percentage !== undefined && (
                            <div style={{ 
                                fontSize: '14px', 
                                color: '#aaaaaa',
                                marginBottom: '8px',
                            }}>
                                {percentage}%
                                {progress?.currentIndex !== undefined && progress?.total && (
                                    <span> ({progress.currentIndex} / {progress.total})</span>
                                )}
                            </div>
                        )}
                        {progress?.size && (
                            <div style={{ 
                                fontSize: '12px', 
                                color: '#888888',
                                marginBottom: '12px',
                            }}>
                                {progress.size}
                            </div>
                        )}
                        <div style={{
                            width: '100%',
                            maxWidth: '400px',
                            height: '4px',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '2px',
                            overflow: 'hidden',
                            marginTop: '8px',
                        }}>
                            {percentage !== undefined ? (
                                <div style={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    backgroundColor: '#4a9eff',
                                    transition: 'width 0.3s ease',
                                }} />
                            ) : (
                                <div style={{
                                    width: '40%',
                                    height: '100%',
                                    backgroundColor: '#4a9eff',
                                    animation: 'loading-indeterminate 1.5s ease-in-out infinite',
                                }} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            </>
        );
    }
}

// Camera Mode Selector Component
class CameraModeSelector extends React.Component<{ 
    mode: 'orbit' | 'free',
    onModeChange: (mode: 'orbit' | 'free') => void
}> {
    render() {
        const { mode, onModeChange } = this.props;
        return (
            <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                zIndex: 100,
                display: 'flex',
                gap: '2px',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                borderRadius: '4px',
                padding: '2px',
                fontFamily: 'Proxima Nova Regular, Helvetica Neue, Arial, Helvetica, sans-serif',
            }}>
                <button
                    onClick={() => onModeChange('orbit')}
                    style={{
                        padding: '4px 8px',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        backgroundColor: mode === 'orbit' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                        color: mode === 'orbit' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                    }}
                    title="Orbit around aircraft center (WASD rotates view, scroll zooms)"
                >
                    Orbit
                </button>
                <button
                    onClick={() => onModeChange('free')}
                    style={{
                        padding: '4px 8px',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        backgroundColor: mode === 'free' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                        color: mode === 'free' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                    }}
                    title="Free-fly camera (WASD moves, mouse looks)"
                >
                    Free
                </button>
            </div>
        );
    }
}

// Camera Position Overlay Component
class CameraPositionOverlay extends React.Component<{ visible: boolean }> {
    state = {
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        projection: 'Perspective'
    };

    updateInterval: number = null;

    componentDidMount() {
        // Update camera position every 100ms
        this.updateInterval = setInterval(() => {
            if ((window as any).viewer && (window as any).viewer.camera) {
                const camera = (window as any).viewer.camera;
                const pos = camera.getPosition();
                const rot = camera.getEulerAngles();
                const proj = camera.camera.projection === 1 ? 'Orthographic' : 'Perspective';
                
                this.setState({
                    position: { 
                        x: Math.round(pos.x * 100) / 100, 
                        y: Math.round(pos.y * 100) / 100, 
                        z: Math.round(pos.z * 100) / 100 
                    },
                    rotation: { 
                        x: Math.round(rot.x * 100) / 100, 
                        y: Math.round(rot.y * 100) / 100, 
                        z: Math.round(rot.z * 100) / 100 
                    },
                    projection: proj
                });
            }
        }, 100);
    }

    componentWillUnmount() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
    }

    render() {
        if (!this.props.visible) {
            return null;
        }

        const { position, rotation, projection } = this.state;
        return (
            <div id="camera-position-overlay">
                <div className="camera-info-title">CAMERA INFO</div>
                <div className="camera-info-section">
                    <div className="camera-info-label">Position:</div>
                    <div className="camera-info-value">
                        X: {position.x} Y: {position.y} Z: {position.z}
                    </div>
                </div>
                <div className="camera-info-section">
                    <div className="camera-info-label">Rotation:</div>
                    <div className="camera-info-value">
                        X: {rotation.x}° Y: {rotation.y}° Z: {rotation.z}°
                    </div>
                </div>
                <div className="camera-info-section">
                    <div className="camera-info-label">Projection:</div>
                    <div className="camera-info-value">{projection}</div>
                </div>
            </div>
        );
    }
}

class App extends React.Component<{ observer: Observer }> {
    state: ObserverData = null
    isInIframe: boolean = false;

    canvasRef: any;
    
    manifestDialogState: { visible: boolean } = { visible: false };
    aboutDialogState: { visible: boolean } = { visible: false };

    constructor(props: any) {
        super(props);

        this.canvasRef = React.createRef();
        this.isInIframe = window.self !== window.top; // Detect if running in iframe
        this.state = this._retrieveState();

        props.observer.on('*:set', () => {
            // update the state
            this.setState(this._retrieveState());
        });
        
        // Listen for manifest import completion
        // Disabled: Don't show popup when manifest is loaded (we inject it programmatically)
        // props.observer.on('manifestData:set', () => {
        //     const manifestData = this.props.observer.get('manifestData');
        //     if (manifestData?.loaded && manifestData?.manifest) {
        //         this.manifestDialogState.visible = true;
        //         this.forceUpdate();
        //     }
        // });
    }

    showAboutDialog = () => {
        this.aboutDialogState.visible = true;
        this.forceUpdate();
    };

    _retrieveState = () => {
        const state: any = {};
        (this.props.observer as any)._keys.forEach((key: string) => {
            state[key] = this.props.observer.get(key);
        });
        return state;
    };

    _setStateProperty = (path: string, value: string) => {
        this.props.observer.set(path, value);
    };

    render() {
        return <div id="application-container">
            <MenuBar 
                observerData={this.state} 
                setProperty={this._setStateProperty}
                onShowAbout={this.showAboutDialog}
            />
            <div id="application-main">
                <Container id="panel-left" flex resizable='right' resizeMin={220} resizeMax={800}>
                    <div className="header" style={{ display: 'none' }}>
                        <div id="title">
                            <img src={'static/logo.png'}/>
                            <div>{`INFINITE FLIGHT MODEL VIEWER v${appVersion}`}</div>
                        </div>
                    </div>
                    <div id="panel-toggle">
                        <img src={'static/logo.png'}/>
                    </div>
                    <LeftPanel observerData={this.state} setProperty={this._setStateProperty} isInIframe={this.isInIframe} />
                </Container>
                <div id='canvas-wrapper'>
                    <canvas id="application-canvas" ref={this.canvasRef} />
                    <CameraModeSelector 
                        mode={this.state?.camera?.uiCameraMode || 'orbit'} 
                        onModeChange={(mode) => this._setStateProperty('camera.uiCameraMode', mode)}
                    />
                    <CameraPositionOverlay visible={this.state?.ui?.cameraInfoVisible === true} />
                    {!this.isInIframe && !this.state?.ui?.loadingOverlay && (!this.state?.scene?.urls || this.state.scene.urls.length === 0) && (
                        <LoadControls setProperty={this._setStateProperty}/>
                    )}
                    <SelectedNode sceneData={this.state.scene} setProperty={this._setStateProperty} />
                    <PopupPanel observerData={this.state} setProperty={this._setStateProperty} />
                    <ErrorBox observerData={this.state} />
                    <Spinner id="spinner" size={30} hidden={true} />
                    {this.state?.ui?.loadingOverlay && (
                        <LoadingOverlay 
                            message={this.state.ui.loadingMessage || 'Loading...'} 
                            progress={this.state.ui.loadingProgress}
                        />
                    )}
                    {this.manifestDialogState.visible && this.state?.manifestData && (
                        <ManifestDialog 
                            manifestData={this.state.manifestData} 
                            onClose={() => {
                                this.manifestDialogState.visible = false;
                                this.forceUpdate();
                            }} 
                        />
                    )}
                    {this.aboutDialogState.visible && (
                        <AboutDialog 
                            onClose={() => {
                                this.aboutDialogState.visible = false;
                                this.forceUpdate();
                            }} 
                        />
                    )}
                </div>
            </div>
        </div>;
    }
}

export default (observer: Observer) => {
    // render out the app
    ReactDOM.render(
        <App observer={observer}/>,
        document.getElementById('app')
    );
};
